import { Link } from "react-router-dom";
import { useState } from "react";
import {
    Search,
    Building2,
    ShieldCheck,
    Coins,
    Eye,
    ClipboardCheck,
    ClipboardList,
    FileText,
    Users,
    ArrowRight,
    Briefcase,
    Landmark,
    HardHat,
    CalendarClock,
    Award,
    MapPin,
    Clock,
} from "lucide-react";
import useReveal from "../hooks/useReveal";
import { site } from "../data/site";

/**
 * HomeSectionsV2 — the six new home-page sections introduced with the v2.0
 * Corporate Release brief, in the exact order the brief specifies.
 *
 *   1. Orientation (2 cards)      — Roof Assessments · External Building Assessments
 *   2. WhyIndependence (4 blocks)
 *   3. ProfessionalReporting      — four labels, image-slot structure
 *   4. GuardianshipJourneySummary — link out to /guardian-program
 *   5. SixStepProcess             — Assess · Report · Scope · Tender · Oversee · Monitor
 *   6. AudienceSplit              — For Managers / For Committees
 *   7. WhySRM                     — 6-item evidence grid from site.whyChoose
 *
 * Every section uses the approved SRM colour system (navy #0F2856, gold
 * #C9A55C, white, light-grey) and existing typography classes. No new
 * fonts, gradients or accent colours introduced.
 */

const EYEBROW =
    "font-display font-bold tracking-[0.24em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

const ICONS = {
    Search, Building2, ShieldCheck, Coins, Eye, ClipboardCheck,
    ClipboardList, FileText, Users, Briefcase, Landmark, HardHat, CalendarClock,
};

/* ────────────────────────────────────────────────────────────
   CardPanel — restrained SVG placeholder for the two orientation
   service cards. Approved SRM navy/blue palette only. No embedded
   text, no photograph, no logo variant. Two distinct symbols so
   each service reads within two seconds.
   ──────────────────────────────────────────────────────────── */
function CardPanel({ variant }) {
    const isRoof = variant === "roof";
    return (
        <div
            aria-hidden
            data-placeholder="pending-srm-photo"
            data-panel-variant={variant}
            className="relative h-56 lg:h-64 w-full overflow-hidden bg-srm-navy"
        >
            {/* Subtle radial highlight for depth (approved navy only) */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-70"
                style={{
                    background:
                        "radial-gradient(120% 120% at 20% 20%, rgba(255,255,255,0.06) 0%, rgba(15,40,86,0) 55%)",
                }}
            />
            {isRoof ? (
                /* ROOF PLAN SVG — pitched profile + roof sheet grid + assessment magnifier */
                <svg
                    viewBox="0 0 320 180"
                    className="absolute inset-0 h-full w-full"
                    preserveAspectRatio="xMidYMid slice"
                    role="img"
                >
                    {/* horizon linework */}
                    <line x1="0" y1="140" x2="320" y2="140" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
                    <line x1="0" y1="150" x2="320" y2="150" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                    {/* Pitched-roof profile — two hip planes meeting at a ridge */}
                    <g stroke="#C9A55C" strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round">
                        <path d="M40 118 L160 46 L280 118" />
                        {/* Ridge highlight */}
                        <line x1="160" y1="46" x2="160" y2="118" stroke="rgba(201,165,92,0.45)" strokeWidth="1" strokeDasharray="3 4" />
                        {/* Eave line */}
                        <line x1="30" y1="118" x2="290" y2="118" strokeWidth="1.2" />
                        {/* Roof sheet layout — parallel lines running down each pitch */}
                        <g stroke="rgba(201,165,92,0.35)" strokeWidth="0.8">
                            <line x1="70" y1="118" x2="130" y2="82" />
                            <line x1="90" y1="118" x2="140" y2="88" />
                            <line x1="110" y1="118" x2="150" y2="94" />
                            <line x1="190" y1="82" x2="250" y2="118" />
                            <line x1="180" y1="88" x2="230" y2="118" />
                            <line x1="170" y1="94" x2="210" y2="118" />
                        </g>
                        {/* Two penetrations (vent / duct on the roof) */}
                        <g stroke="rgba(255,255,255,0.55)" strokeWidth="1">
                            <rect x="110" y="94" width="10" height="8" fill="none" />
                            <rect x="200" y="94" width="10" height="8" fill="none" />
                        </g>
                    </g>

                    {/* Assessment magnifier over one pitch — clearly a 'search' cue */}
                    <g stroke="#C9A55C" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="228" cy="72" r="14" />
                        <line x1="238" y1="82" x2="252" y2="96" />
                        {/* Cross-hair inside the magnifier */}
                        <line x1="221" y1="72" x2="235" y2="72" stroke="rgba(201,165,92,0.6)" strokeWidth="1" />
                        <line x1="228" y1="65" x2="228" y2="79" stroke="rgba(201,165,92,0.6)" strokeWidth="1" />
                    </g>

                    {/* Corner accent */}
                    <rect x="0" y="0" width="4" height="60" fill="#C9A55C" opacity="0.55" />
                </svg>
            ) : (
                /* BUILDING ELEVATION SVG — multi-storey with balconies, columns of windows and a ground-level magnifier */
                <svg
                    viewBox="0 0 320 180"
                    className="absolute inset-0 h-full w-full"
                    preserveAspectRatio="xMidYMid slice"
                    role="img"
                >
                    {/* Ground line */}
                    <line x1="0" y1="162" x2="320" y2="162" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
                    <line x1="0" y1="168" x2="320" y2="168" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                    {/* Building block — 5 storeys */}
                    <g stroke="#C9A55C" strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round">
                        <rect x="88" y="28" width="164" height="134" />
                        {/* Storey slabs */}
                        <line x1="88" y1="55" x2="252" y2="55" strokeWidth="1.2" />
                        <line x1="88" y1="82" x2="252" y2="82" strokeWidth="1.2" />
                        <line x1="88" y1="109" x2="252" y2="109" strokeWidth="1.2" />
                        <line x1="88" y1="136" x2="252" y2="136" strokeWidth="1.2" />
                        {/* Vertical facade seam (cladding column separator) */}
                        <line x1="170" y1="28" x2="170" y2="162" stroke="rgba(201,165,92,0.35)" strokeWidth="0.9" strokeDasharray="3 4" />
                    </g>

                    {/* Window grid — 5 storeys × 4 columns */}
                    <g stroke="rgba(255,255,255,0.55)" strokeWidth="0.9" fill="none">
                        {[36, 63, 90, 117, 144].map((y) => (
                            <g key={y}>
                                <rect x="98"  y={y} width="18" height="15" />
                                <rect x="128" y={y} width="18" height="15" />
                                <rect x="180" y={y} width="18" height="15" />
                                <rect x="210" y={y} width="18" height="15" />
                            </g>
                        ))}
                    </g>

                    {/* Balcony rails on floors 2, 3, 4 (left column pair) */}
                    <g stroke="rgba(201,165,92,0.55)" strokeWidth="1" fill="none">
                        <line x1="94" y1="82" x2="152" y2="82" />
                        <line x1="94" y1="109" x2="152" y2="109" />
                        <line x1="94" y1="136" x2="152" y2="136" />
                        {/* Vertical rail balusters — one per level */}
                        <g strokeWidth="0.6">
                            {[82, 109, 136].map((y) =>
                                [100, 112, 124, 136, 148].map((x) => (
                                    <line key={`${x}-${y}`} x1={x} y1={y} x2={x} y2={y + 5} />
                                ))
                            )}
                        </g>
                    </g>

                    {/* Ground-level assessor magnifier (compact) */}
                    <g stroke="#C9A55C" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="42" cy="140" r="14" />
                        <line x1="52" y1="150" x2="66" y2="164" />
                        <line x1="35" y1="140" x2="49" y2="140" stroke="rgba(201,165,92,0.6)" strokeWidth="1" />
                        <line x1="42" y1="133" x2="42" y2="147" stroke="rgba(201,165,92,0.6)" strokeWidth="1" />
                    </g>

                    {/* Corner accent */}
                    <rect x="0" y="0" width="4" height="60" fill="#C9A55C" opacity="0.55" />
                </svg>
            )}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────
   1. ORIENTATION — 2-card headline services below the hero
   ──────────────────────────────────────────────────────────── */
export function Orientation() {
    const ref = useReveal();
    /* Approved SRM assessment photography — placed 2026-02.
       Both files supplied by SRM and stored in /app/frontend/public/assets/.
       Preserve the assessor, SRM shirt branding, harness, tablet and
       building context when adjusting focal position. */
    const CARDS = [
        {
            slug: "roof-assessments",
            Icon: Search,
            title: "Independent Roof Assessments",
            body: "Independent roof condition assessments, professional reporting and long-term roof asset guidance.",
            cta: "Explore Roof Assessments",
            to: "/services#roof-assessments",
            photo: "/assets/srm-roof-assessment.png",
            alt: "SRM assessor in high-vis harness and hard hat carrying out an independent roof assessment on a strata rooftop with a tablet, modern strata buildings visible under a bright Australian sky.",
            focal: "center 30%",
        },
        {
            slug: "external-building-assessments",
            Icon: Building2,
            title: "Independent Building Assessments",
            body: "Independent assessments of external building elements, defects and maintenance priorities.",
            cta: "Explore Building Assessments",
            to: "/services#external-building-assessments",
            photo: "/assets/srm-building-assessment.png",
            alt: "SRM assessor in an SRM navy shirt and cap carrying a clipboard, conducting an independent external building assessment at ground level in front of a modern strata building with balconies, glazing and cladding.",
            focal: "center 40%",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-orientation"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-24">
                <div className="max-w-3xl reveal">
                    <div className={EYEBROW}>Where the SRM Relationship Can Begin</div>
                    <span aria-hidden className={`mt-4 sm:mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-6 sm:mt-8 font-display font-bold text-[1.7rem] sm:text-[2.15rem] lg:text-[2.55rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        Independent assessment.
                        <br className="hidden sm:block" />
                        <span> </span>A clear starting point.
                    </h2>
                    <p className="mt-5 sm:mt-6 text-[14.5px] sm:text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.7] lg:leading-[1.75] max-w-[640px]">
                        Independent assessment is a common starting point, but
                        SRM may also assist later where suitable project
                        information exists.
                    </p>
                </div>
                <div className="mt-8 sm:mt-12 lg:mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                    {CARDS.map((c, i) => (
                        <article
                            key={c.slug}
                            data-testid={`orientation-card-${i}`}
                            className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold" />
                            <div className="relative h-40 sm:h-48 lg:h-64 w-full overflow-hidden bg-srm-navy">
                                <img
                                    src={c.photo}
                                    alt={c.alt}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover"
                                    style={{ objectPosition: c.focal || "center" }}
                                />
                            </div>
                            <div className="flex-1 flex flex-col p-6 sm:p-7 lg:p-10">
                                <span className="inline-flex h-11 w-11 sm:h-12 sm:w-12 lg:h-14 lg:w-14 items-center justify-center rounded-full border border-srm-navy/15 text-srm-navy transition-colors duration-500 group-hover:border-srm-gold group-hover:text-srm-gold">
                                    <c.Icon className="h-5 w-5 lg:h-6 lg:w-6" strokeWidth={1.6} />
                                </span>
                                <h3 className="mt-4 sm:mt-5 lg:mt-6 font-display font-bold text-srm-navy text-[1.2rem] sm:text-[1.3rem] lg:text-[1.55rem] tracking-[-0.012em] leading-tight">
                                    {c.title}
                                </h3>
                                <p className="mt-3 sm:mt-4 text-[13.5px] sm:text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.65] lg:leading-[1.75]">
                                    {c.body}
                                </p>
                                <Link
                                    to={c.to}
                                    data-testid={`orientation-cta-${i}`}
                                    className="mt-6 lg:mt-8 inline-flex items-center gap-2 pt-0 text-[11px] font-semibold tracking-[0.22em] uppercase text-srm-navy hover:text-srm-gold transition-colors duration-300"
                                >
                                    {c.cta}
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   2. WHY INDEPENDENCE MATTERS
   One heading, one paragraph, three short supporting points.
   ──────────────────────────────────────────────────────────── */
export function WhyIndependenceMatters() {
    const ref = useReveal();
    const POINTS = [
        {
            icon: "ShieldCheck",
            title: "Property-focused advice",
            body: "Guidance shaped by the condition of your building — not by any repair scope.",
        },
        {
            icon: "ClipboardCheck",
            title: "Evidence-based decisions",
            body: "Findings supported by observed condition and photographic evidence.",
        },
        {
            icon: "Users",
            title: "Comparable scopes & tenders",
            body: "Defined scopes so contractors can price the same work on a like-for-like basis.",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-why-independence"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-20 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-5 reveal">
                        <div className={EYEBROW}>Why Independence Matters</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.65rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            Advice you can rely on, purely focused on your building.
                        </h2>
                        <p className="mt-7 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.75]">
                            SRM is an independent advisory practice. Every recommendation
                            is shaped by the observed condition of the building and the
                            committee&apos;s interests.
                        </p>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
                        {POINTS.map((it, i) => {
                            const Icon = ICONS[it.icon] || ShieldCheck;
                            return (
                                <article
                                    key={it.title}
                                    data-testid={`independence-block-${i}`}
                                    className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-6 lg:p-7 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                                    style={{ transitionDelay: `${i * 70}ms` }}
                                >
                                    <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold" />
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-srm-gold/50 text-srm-gold">
                                        <Icon className="h-5 w-5" strokeWidth={1.7} />
                                    </span>
                                    <h3 className="mt-5 font-display font-bold text-srm-navy text-[1.02rem] lg:text-[1.08rem] leading-tight">
                                        {it.title}
                                    </h3>
                                    <p className="mt-3 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.7]">
                                        {it.body}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   3. PROFESSIONAL REPORTING (structural slots, no fake content)
   ──────────────────────────────────────────────────────────── */
export function ProfessionalReporting() {
    const ref = useReveal();
    const [reportType, setReportType] = useState("roof");
    const isRoof = reportType === "roof";
    const LABELS = [
        { title: "Clear Condition Summary",  body: "Understand the overall condition quickly." },
        { title: "Photographic Evidence",    body: "See the issue and its location." },
        { title: "Prioritised Actions",      body: "Know what requires attention first." },
        { title: "Committee-Ready Output",   body: "Use the report to support planning and decisions." },
    ];
    const ROOF_BULLETS = [
        "Executive Summary",
        "Roof Condition Overview",
        "Zone-by-Zone Findings",
        "Photographic Evidence",
        "Risk / Priority Classification",
        "Recommended Action",
    ];
    const BUILDING_BULLETS = [
        "Executive Summary",
        "Building Health Snapshot",
        "External Elevation Findings",
        "Defect Photographs",
        "Priority Actions",
        "Recommended Maintenance",
    ];
    const bullets = isRoof ? ROOF_BULLETS : BUILDING_BULLETS;
    return (
        <section
            ref={ref}
            data-testid="home-professional-reporting"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-16 items-start">
                    {/* Report preview slot — awaiting approved client-report imagery.
                        Renders as a professional navy panel with the SRM signature
                        rule so the section reads as intentional design, not a
                        broken placeholder. */}
                    <div className="lg:col-span-6 reveal">
                        {/* Report-type switcher — single rounded outer container, two equal buttons */}
                        <div
                            role="tablist"
                            aria-label="Report type"
                            data-testid="report-type-toggle"
                            className="mb-6 grid grid-cols-2 max-[359px]:grid-cols-1 gap-1 p-1 max-w-[520px] rounded-md border border-srm-line bg-[#EFEDE6] shadow-[0_2px_10px_rgba(15,40,86,0.06)]"
                        >
                            {[
                                { key: "roof", label: "Roof Assessment Report" },
                                { key: "building", label: "Building Assessment Report" },
                            ].map((opt) => {
                                const active = reportType === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        data-testid={`report-type-${opt.key}`}
                                        onClick={() => setReportType(opt.key)}
                                        className={
                                            "relative w-full min-h-[44px] h-11 rounded-sm text-[11px] font-semibold tracking-[0.2em] uppercase " +
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-srm-gold focus-visible:ring-offset-1 " +
                                            "transition-[background-color,color,box-shadow,transform,border-color] duration-150 ease-out " +
                                            (active
                                                ? "bg-srm-navy text-white shadow-[0_4px_14px_rgba(15,40,86,0.22)]"
                                                : "bg-white text-srm-navy border border-srm-line hover:bg-[#F5F3EB] hover:-translate-y-[1px] hover:border-srm-navy/25")
                                        }
                                    >
                                        {opt.label}
                                        {active && (
                                            <span
                                                aria-hidden
                                                className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-[6px] h-[2px] w-8 bg-srm-gold"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="relative aspect-[4/5] w-full rounded-2xl overflow-hidden bg-srm-navy ring-1 ring-srm-navy/10 shadow-[0_28px_60px_rgba(15,40,86,0.18)]">
                            {/* Subtle document-page watermark — generic layout, no client info */}
                            <svg
                                aria-hidden
                                viewBox="0 0 400 500"
                                preserveAspectRatio="xMidYMid slice"
                                className="absolute inset-0 h-full w-full opacity-[0.055]"
                            >
                                <rect x="60" y="40" width="280" height="420" fill="none" stroke="#ffffff" strokeWidth="1.4" />
                                <rect x="80" y="72" width="120" height="8" fill="#ffffff" />
                                <rect x="80" y="94" width="200" height="4" fill="#ffffff" />
                                <rect x="80" y="106" width="180" height="4" fill="#ffffff" />
                                <rect x="80" y="140" width="240" height="70" fill="none" stroke="#ffffff" strokeWidth="1" />
                                <rect x="80" y="228" width="60" height="4" fill="#ffffff" />
                                {[248, 260, 272, 284, 296, 308, 320, 332].map((y) => (
                                    <rect key={y} x="80" y={y} width={220 - ((y - 248) % 3) * 20} height="3" fill="#ffffff" />
                                ))}
                                <rect x="80" y="360" width="240" height="4" fill="#ffffff" />
                                {[378, 390, 402, 414, 426].map((y) => (
                                    <rect key={y} x="80" y={y} width={200 - ((y - 378) % 4) * 15} height="3" fill="#ffffff" />
                                ))}
                            </svg>
                            <div className="absolute inset-0 bg-gradient-to-b from-srm-navy/40 via-srm-navy/70 to-srm-navy" />
                            <div className="absolute inset-0 p-10 lg:p-12 flex flex-col">
                                <span aria-hidden className="h-[3px] w-16 bg-srm-gold" />
                                <div className="mt-6 font-display font-bold tracking-[0.24em] uppercase text-srm-gold text-[11px]">
                                    SRM Report &middot; {isRoof ? "Roof Assessment" : "External Building Assessment"}
                                </div>
                                <div className="mt-10 flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="font-display font-bold text-white text-[1.7rem] lg:text-[1.85rem] tracking-[-0.012em] leading-[1.15]">
                                            {isRoof
                                                ? "Independent roof assessment reporting."
                                                : "Independent external building reporting."}
                                        </div>
                                        <p className="mt-5 text-white/70 text-[13.5px] lg:text-[14px] leading-[1.7] max-w-[380px]">
                                            {isRoof
                                                ? "Observed condition, prioritised actions and photographic evidence \u2014 committee-ready."
                                                : "External elevation findings, priority defects and recommended maintenance \u2014 committee-ready."}
                                        </p>
                                    </div>
                                    <ul className="mt-6 space-y-3 text-white/85 text-[12.5px] tracking-[0.02em]">
                                        {bullets.map((b) => (
                                            <li key={b} className="flex items-center gap-3">
                                                <span aria-hidden className="h-1 w-6 bg-srm-gold" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-6 reveal" style={{ transitionDelay: "100ms" }}>
                        <div className={EYEBROW}>Professional Reporting</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.65rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            Reporting built for real decisions.
                        </h2>
                        <p className="mt-6 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8]">
                            Clear priorities, practical recommendations and committee-ready evidence.
                        </p>
                        <p className="mt-4 text-[13.5px] lg:text-[14px] text-srm-body/85 leading-[1.75] italic">
                            Where appropriate, SRM can discuss a suitable fictional
                            sample report as part of an initial conversation.
                        </p>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {LABELS.map((l, i) => (
                                <div
                                    key={l.title}
                                    data-testid={`reporting-label-${i}`}
                                    className="relative rounded-xl bg-white ring-1 ring-srm-line p-6"
                                >
                                    <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-10 bg-srm-gold" />
                                    <h3 className="font-display font-bold text-srm-navy text-[0.95rem] tracking-[-0.005em]">{l.title}</h3>
                                    <p className="mt-2 text-[13px] text-srm-body leading-[1.65]">{l.body}</p>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/contact"
                            data-testid="reporting-cta"
                            className="mt-10 inline-flex items-center gap-2 rounded-sm bg-transparent text-srm-navy text-[11.5px] font-semibold tracking-[0.22em] uppercase px-8 h-[50px] border border-srm-navy/30 hover:bg-srm-navy hover:text-white hover:border-srm-navy transition-all duration-300"
                        >
                            Discuss SRM Reporting
                            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   4. GUARDIANSHIP JOURNEY — the trust section.
   Left: one emotional advisory-meeting image.
   Right: the story — heading, introduction, single connected
   vertical timeline of four stages joined by one gold line,
   closing statement, and CTA. No cards. No icons. One relationship.
   ──────────────────────────────────────────────────────────── */
export function GuardianshipJourneySummary() {
    const ref = useReveal();
    const STAGES = [
        {
            title: "Independent Assessment",
            lines: ["Understand the condition of your roof."],
        },
        {
            title: "Independent Scope & Tender",
            lines: ["Prepare the scope.", "Compare contractors fairly."],
        },
        {
            title: "Independent Delivery Oversight",
            lines: ["Protect quality.", "Review variations.", "Monitor completion."],
        },
        {
            title: "Ongoing Guardianship",
            lines: ["Annual assessments.", "Planning.", "Long-term roof care."],
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-guardianship-summary"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* LEFT — emotional advisory-meeting image (sticky on desktop
                        so it stays beside the reader while they move through the
                        journey — the trusted adviser sitting beside the committee) */}
                    <div
                        className="lg:col-span-5 reveal"
                        data-testid="guardianship-image"
                    >
                        <div className="lg:sticky lg:top-28">
                            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl ring-1 ring-srm-line shadow-[0_28px_60px_rgba(15,40,86,0.14)] bg-srm-navy">
                                <img
                                    src="/assets/srm-meeting.png"
                                    alt="SRM advisers meeting with a strata committee, reviewing an independent roof report together on a table — professional, calm, decision-making moment."
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover"
                                    style={{ objectPosition: "center 40%" }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — the story */}
                    <div className="lg:col-span-7 reveal" style={{ transitionDelay: "80ms" }}>
                        <div className="font-display font-black tracking-[0.24em] uppercase text-srm-gold text-[14px] lg:text-[15.5px]">
                            The Guardianship Journey
                        </div>
                        <span aria-hidden className="mt-5 block h-[3px] w-24 bg-srm-gold" />
                        <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.65rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            One independent partner.
                            <br />
                            For the life of your roof.
                        </h2>
                        <p className="mt-7 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8] max-w-[560px]">
                            A roof assessment is only the beginning. The real value comes
                            from having an independent partner who remains beside you
                            through planning, procurement, delivery and long-term
                            guardianship.
                        </p>

                        {/* Connected vertical timeline — one relationship, not four products */}
                        <ol
                            data-testid="guardianship-timeline"
                            className="mt-12 relative pl-8"
                        >
                            {/* Single gold vertical line — starts at first marker, ends at last */}
                            <span
                                aria-hidden
                                className="absolute left-[5px] top-[6px] bottom-[6px] w-px bg-srm-gold"
                            />
                            {STAGES.map((s, i) => (
                                <li
                                    key={s.title}
                                    data-testid={`journey-stage-${i}`}
                                    className={`relative ${i < STAGES.length - 1 ? "pb-10 lg:pb-12" : ""}`}
                                >
                                    {/* Marker — small gold dot on the line */}
                                    <span
                                        aria-hidden
                                        className="absolute -left-[30px] top-[6px] h-[11px] w-[11px] rounded-full bg-srm-gold ring-4 ring-[#F7F5F0]"
                                    />
                                    <h3 className="font-display font-bold text-srm-navy text-[1.1rem] lg:text-[1.2rem] tracking-[-0.005em] leading-tight">
                                        {s.title}
                                    </h3>
                                    <div className="mt-2 space-y-1 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.7]">
                                        {s.lines.map((line) => (
                                            <p key={line}>{line}</p>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ol>

                        {/* Closing statement — the reason SRM exists after the assessment */}
                        <div className="mt-12 border-t border-srm-line pt-8">
                            <p className="font-display text-srm-navy text-[1.15rem] lg:text-[1.3rem] leading-[1.5] tracking-[-0.008em] max-w-[560px]">
                                We remain independent so every important roofing decision is
                                made in your best interests.
                            </p>
                        </div>

                        <div className="mt-10">
                            <Link
                                to="/guardian-program"
                                data-testid="journey-cta"
                                className="group inline-flex items-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.22em] uppercase px-8 h-[50px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300"
                            >
                                Explore the Guardianship Program
                                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   5. SIX-STEP PROCESS  Assess · Report · Scope · Tender · Oversee · Monitor
   ──────────────────────────────────────────────────────────── */
export function SixStepProcess() {
    const ref = useReveal();
    const steps = (site.howItWorks && site.howItWorks.steps) || [];
    return (
        <section
            ref={ref}
            data-testid="home-six-step"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>The SRM Process</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        From first assessment to long-term care.
                    </h2>
                </div>
                <ol className="mt-16 lg:mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 lg:gap-6">
                    {steps.map((s, i) => {
                        const Icon = ICONS[s.icon] || Search;
                        return (
                            <li
                                key={s.title}
                                data-testid={`process-step-${i}`}
                                className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-6 lg:p-7 shadow-[0_4px_18px_rgba(15,40,86,0.05)] text-center"
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold" />
                                <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full border border-srm-gold/50 text-srm-gold">
                                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                                </span>
                                <div className="mt-4 font-display font-bold tracking-[0.2em] uppercase text-srm-navy/50 text-[10.5px]">
                                    Step {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="mt-2 font-display font-bold text-srm-navy text-[1rem] lg:text-[1.05rem] leading-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-[12.5px] text-srm-body leading-[1.6]">{s.body}</p>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   6. AUDIENCE SPLIT — For Managers / For Committees
   ──────────────────────────────────────────────────────────── */
export function AudienceSplit() {
    const ref = useReveal();
    const LEFT = {
        Icon: Briefcase,
        eyebrow: "For Body Corporate Managers",
        items: [
            "Clear professional documentation",
            "Committee-ready reports",
            "Independent technical support",
        ],
    };
    const RIGHT = {
        Icon: Landmark,
        eyebrow: "For Committees",
        items: [
            "Understand the actual condition",
            "Plan maintenance expenditure",
            "Protect long-term asset value",
        ],
    };
    return (
        <section
            ref={ref}
            data-testid="home-audience-split"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Built for Managers and Committees</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.65rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        One partner. Both audiences served.
                    </h2>
                </div>
                <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    {[LEFT, RIGHT].map((col, colIdx) => (
                        <article
                            key={col.eyebrow}
                            data-testid={`audience-col-${colIdx}`}
                            className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-9 lg:p-11 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                            style={{ transitionDelay: `${colIdx * 100}ms` }}
                        >
                            <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold" />
                            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-srm-gold/50 text-srm-gold">
                                <col.Icon className="h-6 w-6" strokeWidth={1.6} />
                            </span>
                            <div className="mt-6 font-display font-bold tracking-[0.22em] uppercase text-srm-navy/60 text-[11px]">
                                {col.eyebrow}
                            </div>
                            <ul className="mt-6 space-y-3">
                                {col.items.map((it) => (
                                    <li key={it} className="flex items-start gap-3 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.65]">
                                        <span aria-hidden className="mt-2 inline-block h-1 w-4 bg-srm-gold shrink-0" />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   7. WHY SRM — 6-item evidence grid from site.whyChoose
   ──────────────────────────────────────────────────────────── */
export function WhySRM() {
    const ref = useReveal();
    const items = site.whyChoose || [];
    return (
        <section
            ref={ref}
            data-testid="home-why-srm"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl reveal">
                    <div className={EYEBROW}>Why Strata Communities Choose SRM</div>
                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Independent by design. Practical by experience.
                    </h2>
                </div>
                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {items.map((it, i) => {
                        const Icon = ICONS[it.icon] || ShieldCheck;
                        return (
                            <article
                                key={it.title}
                                data-testid={`why-srm-${i}`}
                                className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                                style={{ transitionDelay: `${i * 70}ms` }}
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold" />
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-srm-gold/50 text-srm-gold">
                                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                                </span>
                                <h3 className="mt-5 font-display font-bold text-srm-navy text-[1.05rem] lg:text-[1.12rem] leading-tight">
                                    {it.title}
                                </h3>
                                <p className="mt-3 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.7]">
                                    {it.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────────────────────────────────────
   HOMEPAGE MARKETING-FLOW SECTIONS (2026-08 pass)
   The story hierarchy required by the release-candidate brief.
   ──────────────────────────────────────────────────────────── */

/* 1. Guardian proposition — the first section immediately after hero.
   Short. Uncluttered. No photograph, no card grid, no analogy. */
export function GuardianProposition() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="home-guardian-proposition"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="reveal max-w-3xl mx-auto text-center">
                    <div className={EYEBROW}>The SRM Guardian Model</div>
                    <span aria-hidden className={`mx-auto mt-4 sm:mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-6 sm:mt-8 font-display font-bold text-[1.7rem] sm:text-[2.15rem] lg:text-[2.55rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        One independent partner.
                        <br className="hidden sm:block" />
                        <span> </span>Use the support you need.
                    </h2>
                    <p className="mt-5 sm:mt-7 text-[15px] sm:text-[16px] lg:text-[17px] text-srm-body leading-[1.7] lg:leading-[1.8]">
                        SRM can assess the property, define the work, oversee
                        delivery and maintain the long-term asset record.
                    </p>
                    <p className="mt-4 sm:mt-5 font-display font-semibold tracking-[0.02em] text-srm-navy text-[14.5px] sm:text-[15.5px] lg:text-[16.5px] leading-[1.7]">
                        Engage SRM for one stage, selected stages or the complete
                        Guardian Program.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* 2. Four-stage Guardian pathway — canonical stage names. */
export function FourStagePathway() {
    const ref = useReveal();
    const STAGES = [
        {
            step: "01",
            icon: ClipboardCheck,
            title: "Assessment & Report",
            body: "Understand the condition, risks and priorities.",
        },
        {
            step: "02",
            icon: ClipboardList,
            title: "Scope of Works & Tender",
            body:
                "Define the work and obtain comparable contractor submissions.",
        },
        {
            step: "03",
            icon: Eye,
            title: "Delivery Oversight",
            body:
                "Review progress, quality, variations and completion.",
        },
        {
            step: "04",
            icon: CalendarClock,
            title: "Ongoing Guardianship & Monitoring",
            body:
                "Maintain the asset record and keep future priorities current.",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-four-stage-pathway"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-24">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>From Assessment to Long-Term Guardianship</div>
                    <span aria-hidden className={`mx-auto mt-4 sm:mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-6 sm:mt-8 font-display font-bold text-[1.7rem] sm:text-[2.1rem] lg:text-[2.5rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        A clear pathway.
                        <br className="hidden sm:block" />
                        <span> </span>Choose where SRM supports you.
                    </h2>
                </div>
                <ol className="mt-8 sm:mt-12 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 lg:gap-5">
                    {STAGES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <li
                                key={s.title}
                                data-testid={`home-stage-${i}`}
                                className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-5 sm:p-6 lg:p-7 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-14 sm:w-16 bg-srm-gold" />
                                <div className="flex items-start gap-3.5 sm:gap-4">
                                    <span className="inline-flex h-[44px] w-[44px] sm:h-[52px] sm:w-[52px] shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                        <Icon className="h-[19px] w-[19px] sm:h-[22px] sm:w-[22px]" strokeWidth={1.6} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="font-display font-bold tracking-[0.2em] uppercase text-srm-navy/50 text-[10px] sm:text-[10.5px]">
                                            Stage {s.step}
                                        </div>
                                        <h3 className="mt-1 font-display font-bold text-srm-navy text-[1.02rem] sm:text-[1.08rem] lg:text-[1.12rem] tracking-[-0.005em] leading-snug">
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>
                                <p className="mt-3.5 text-[13.5px] sm:text-[14px] text-srm-body leading-[1.6]">
                                    {s.body}
                                </p>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
}

/* 3. Flexible engagement — three choices and the later-entry clarification. */
export function FlexibleEngagement() {
    const ref = useReveal();
    const CHOICES = [
        {
            eyebrow: "One Stage",
            body: "Support for a specific requirement.",
        },
        {
            eyebrow: "Selected Stages",
            body: "Combine the services relevant to the project.",
        },
        {
            eyebrow: "Complete Guardian Program",
            body:
                "Continuity from assessment through long-term monitoring.",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-flexible-engagement"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <h2 className="font-display font-bold text-[1.7rem] sm:text-[2.05rem] lg:text-[2.4rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        Choose the support you need.
                    </h2>
                    <p className="mt-5 sm:mt-6 text-[14.5px] sm:text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.75]">
                        Each stage stands alone. Engage one stage, combine selected
                        stages or retain SRM across the complete Guardian Program.
                    </p>
                </div>
                <ul className="mt-8 sm:mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 max-w-4xl mx-auto">
                    {CHOICES.map((c, i) => (
                        <li
                            key={c.eyebrow}
                            data-testid={`home-choice-${i}`}
                            className="reveal relative overflow-hidden rounded-xl bg-white ring-1 ring-srm-line px-5 py-4 sm:p-5 lg:p-8 shadow-[0_3px_14px_rgba(15,40,86,0.04)]"
                            style={{ transitionDelay: `${i * 60}ms` }}
                        >
                            <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-full w-[3px] md:h-[3px] md:w-12 md:top-0 md:left-0 bg-srm-gold" />
                            <div className="pl-3 md:pl-0">
                                <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[10.5px]">
                                    {c.eyebrow}
                                </div>
                                <p className="mt-2 sm:mt-3 md:mt-4 text-[14px] lg:text-[14.5px] text-srm-body leading-[1.7]">
                                    {c.body}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
                <p
                    data-testid="home-later-entry-clarification"
                    className="mt-8 sm:mt-10 max-w-3xl mx-auto text-center text-[13px] sm:text-[13.5px] lg:text-[14px] text-srm-body/85 italic leading-[1.65]"
                >
                    Where suitable project information already exists, SRM may
                    enter at a later stage after reviewing it.
                </p>
            </div>
        </section>
    );
}

/* 4. Compact credibility proof — five short proof points, one supporting line. */
export function CompactCredibilityProof() {
    const ref = useReveal();
    const POINTS = [
        { icon: ShieldCheck, label: "Independent property-focused advice" },
        { icon: HardHat, label: "Builder-level technical experience" },
        { icon: FileText, label: "Committee-ready reporting" },
        { icon: Award, label: "Master Builders Queensland membership" },
        { icon: MapPin, label: "Australia-wide capability" },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-credibility-proof"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <h2 className="font-display font-bold text-[1.65rem] sm:text-[2.05rem] lg:text-[2.4rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        Independent expertise behind every stage.
                    </h2>
                </div>
                <ul className="mt-8 sm:mt-10 lg:mt-12 max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-2.5 lg:gap-4">
                    {POINTS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <li
                                key={p.label}
                                data-testid={`home-proof-${i}`}
                                className="reveal flex items-center gap-3 rounded-lg bg-white ring-1 ring-srm-line px-4 py-3 lg:px-5 lg:py-5 shadow-[0_2px_10px_rgba(15,40,86,0.04)]"
                                style={{ transitionDelay: `${i * 40}ms` }}
                            >
                                <span className="inline-flex h-8 w-8 lg:h-9 lg:w-9 shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                    <Icon className="h-[15px] w-[15px] lg:h-[17px] lg:w-[17px]" strokeWidth={1.8} />
                                </span>
                                <span className="text-[13.5px] lg:text-[14px] text-srm-navy font-display font-semibold leading-[1.3]">
                                    {p.label}
                                </span>
                            </li>
                        );
                    })}
                </ul>
                <p className="mt-8 sm:mt-10 max-w-2xl mx-auto text-center text-[13.5px] sm:text-[14px] lg:text-[15px] text-srm-body leading-[1.7]">
                    Recommendations are based on observed condition, documented
                    evidence and the agreed scope of engagement.
                </p>
            </div>
        </section>
    );
}

/* 5. Guardian Program transition — one clear link to /guardian-program. */
export function GuardianProgramTransition() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="home-guardian-transition"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 lg:py-20">
                <div className="max-w-3xl mx-auto reveal">
                    <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-gold/40 p-8 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                        <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold" />
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 items-center">
                            <div>
                                <div className={EYEBROW}>The Guardian Program</div>
                                <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                                <h2 className="mt-4 font-display font-bold text-srm-navy text-[1.4rem] sm:text-[1.6rem] lg:text-[1.85rem] tracking-[-0.014em] leading-[1.2]">
                                    Continuity when your property needs it.
                                </h2>
                                <p className="mt-4 text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.8]">
                                    The complete Guardian Program connects
                                    assessment, procurement support, delivery
                                    oversight and ongoing monitoring through one
                                    independent relationship.
                                </p>
                            </div>
                            <div className="lg:justify-self-end">
                                <Link
                                    to="/guardian-program"
                                    data-testid="home-guardian-transition-cta"
                                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.2em] uppercase text-center px-5 py-3 min-h-[48px] sm:px-8 sm:py-0 sm:min-h-0 sm:h-[48px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300 whitespace-normal sm:whitespace-nowrap"
                                >
                                    Explore the Guardian Program
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* 6. Authority articles — three Amanda Belot pieces in the approved order. */
export function AuthorityArticles() {
    const ref = useReveal();
    const CARDS = [
        {
            slug: "before-the-first-storm-is-your-strata-roof-ready",
            eyebrow: "Storm Readiness",
            readTime: "5 min read",
            title: "Before the First Storm: Is Your Strata Roof Ready?",
        },
        {
            slug: "why-insurance-claims-get-denied",
            eyebrow: "Insurance Readiness",
            readTime: "7 min read",
            title: "Why Insurance Claims Get Denied — and What Smart Committees Do Differently",
        },
        {
            slug: "pros-and-cons-of-roof-guardianship-program",
            eyebrow: "Guardian Program",
            readTime: "9 min read",
            title: "The Pros and Cons of a Roof Guardianship Program for Your Body Corporate",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="home-authority-articles"
            className="bg-[#F7F5F0] border-t border-srm-line"
        >
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Resources</div>
                    <span aria-hidden className={`mx-auto mt-4 sm:mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-6 sm:mt-8 font-display font-bold text-[1.7rem] sm:text-[2.05rem] lg:text-[2.4rem] tracking-[-0.022em] leading-[1.15] text-srm-navy text-balance">
                        Practical guidance for strata decision-makers.
                    </h2>
                </div>
                <div className="mt-8 sm:mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                    {CARDS.map((c, i) => (
                        <Link
                            key={c.slug}
                            to={`/resources/${c.slug}`}
                            data-testid={`home-article-${i}`}
                            className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-5 sm:p-6 lg:p-7 shadow-[0_4px_18px_rgba(15,40,86,0.05)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,40,86,0.12)]"
                            style={{ transitionDelay: `${i * 60}ms` }}
                        >
                            <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold transition-all duration-500 group-hover:w-full" />
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[10.5px]">
                                    {c.eyebrow}
                                </div>
                                <div className="inline-flex items-center gap-1.5 text-srm-navy/55 font-display font-semibold tracking-[0.18em] uppercase text-[9.5px]">
                                    <Clock className="h-3 w-3" strokeWidth={1.8} />
                                    {c.readTime}
                                </div>
                            </div>
                            <span aria-hidden className="mt-3 sm:mt-3.5 block h-[2px] w-10 bg-srm-gold" />
                            <h3 className="mt-3 sm:mt-3.5 font-display font-bold text-srm-navy text-[1.02rem] sm:text-[1.1rem] lg:text-[1.18rem] tracking-[-0.008em] leading-[1.25] group-hover:text-srm-gold transition-colors duration-300">
                                {c.title}
                            </h3>
                            <div className="mt-auto pt-5 sm:pt-6 inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy text-[10.5px] group-hover:text-srm-gold transition-colors duration-300">
                                Read Article
                                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="mt-8 sm:mt-10 flex justify-center">
                    <Link
                        to="/resources"
                        data-testid="home-articles-view-all"
                        className="inline-flex items-center gap-2 rounded-sm bg-transparent text-srm-navy text-[11.5px] font-semibold tracking-[0.2em] uppercase px-8 h-[48px] border border-srm-navy/25 hover:bg-srm-navy hover:text-white hover:border-srm-navy transition-all duration-300"
                    >
                        View All Resources
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

/* 7. Homepage final CTA — one only. */
export function HomeFinalCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="home-final-cta"
            className="bg-srm-navy text-white"
        >
            <div className="container-srm py-16 sm:py-20 lg:py-28">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className="mx-auto block h-[3px] w-16 bg-srm-gold" />
                    <h2
                        data-testid="home-final-cta-headline"
                        className="mt-6 sm:mt-8 font-display font-bold text-[1.75rem] sm:text-[2.25rem] lg:text-[2.85rem] tracking-[-0.022em] leading-[1.15] text-white text-balance"
                    >
                        Start where your property needs support.
                    </h2>
                    <p className="mt-5 sm:mt-7 text-[14.5px] sm:text-[15.5px] lg:text-[17px] text-white/80 leading-[1.7] lg:leading-[1.8] max-w-2xl mx-auto">
                        Whether you need an independent assessment, assistance with
                        a tender, oversight of planned works or continuing
                        Guardianship, speak with the SRM team about the appropriate
                        starting point.
                    </p>
                    <div className="mt-8 sm:mt-10 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="home-final-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[54px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Discuss Your Property
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

